import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Button,
    Chip,
    Box,
    Grid,
    useMediaQuery,
    useTheme
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WebIcon from '@mui/icons-material/Web';
import { AppData } from '../../data/appData';
import './AppCard.scss';

interface AppCardProps {
    app: AppData;
}

const AppCard: React.FC<AppCardProps> = ({ app }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Card className="app-card" elevation={1}>
            <CardMedia
                component="img"
                image={app.icon}
                alt={app.name}
                className="app-icon"
                sx={{
                    backgroundColor: theme.palette.grey[100],
                    transition: 'transform 0.3s',
                    '&:hover': {
                        transform: 'scale(1.05)'
                    }
                }}
            />
            <CardContent>
                <Typography variant="h6" component="div" gutterBottom className="app-title">
                    {app.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" className="app-description">
                    {app.description}
                </Typography>

                {/* <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {app.status.map((status, index) => (
                        <Chip
                            key={index}
                            label={`${status.platform} ${status.version}`}
                            color={status.status === 'Ready for Distribution' ? 'success' : 'warning'}
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                        />
                    ))}
                </Box> */}

                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <Button
                            component={RouterLink}
                            to={`/app/${app.id}`}
                            variant="contained"
                            color="primary"
                            startIcon={<InfoIcon />}
                            sx={{ minWidth: '150px', whiteSpace: 'nowrap' }}
                        >
                            Learn More!
                        </Button>
                    </Box>

                    <Grid container spacing={1} alignItems="center">
                        {app.appStoreLink && (
                            <Grid item xs={isMobile ? 6 : 'auto'}>
                                <a
                                    href={app.appStoreLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="store-badge-link"
                                >
                                    <img
                                        src="/images/app-store-badge.svg"
                                        alt="Download on the App Store"
                                        className="store-badge"
                                        width={isMobile ? "100%" : 120}
                                    />
                                </a>
                            </Grid>
                        )}

                        {app.googlePlayLink && (
                            <Grid item xs={isMobile ? 6 : 'auto'}>
                                <a
                                    href={app.googlePlayLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="store-badge-link"
                                >
                                    <img
                                        src="/images/google-play-badge.svg"
                                        alt="Get it on Google Play"
                                        className="store-badge"
                                        width={isMobile ? "100%" : 120}
                                    />
                                </a>
                            </Grid>
                        )}

                        {app.webLink && (
                            <Grid item xs={isMobile ? 12 : 'auto'}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    href={app.webLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<WebIcon />}
                                    sx={{
                                        mt: isMobile ? 1 : 0,
                                        width: isMobile ? '100%' : 'auto',
                                        borderWidth: '2px',
                                        '&:hover': {
                                            borderWidth: '2px',
                                            transform: 'translateY(-3px)',
                                            transition: 'transform 0.3s'
                                        }
                                    }}
                                >
                                    Web Version
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </CardContent>
        </Card>
    );
};

export default AppCard;